import React, { useState, useContext, useEffect, useRef } from 'react';
import { useApi, getCustomImage } from '@monash/portal-frontend-common';
import * as Sentry from '@sentry/browser';
import { useInterval } from '@/hooks/use-interval';
import c from './clock.module.scss';
import WidgetIcon from '../../widget-library/WidgetIcon';
import preview from './preview.svg';
import { Icon } from '@monash/portal-react';
import { WidgetContext } from '@/components/providers/WidgetProvider';
import { CustomiseClock } from './customise-clock/CustomiseClock';
import useLiveDate from '@/hooks/use-live-date';
import { COLOURS } from './constants';
import { IMAGE_EFFECTS, IMAGE_EFFECTS_BG_SIZE } from '../../../../../constants';

const TIME_UNIT = {
  H: 'hour',
  M: 'minute',
  S: 'second',
};

const Clock = ({
  updateData,
  widgetId,
  data,
  escapeSettingActionsFocusRef,
  onSelectedPage,
}) => {
  // Extract colour code from clock data
  // If a colour cannot be found, use black
  const clockColour =
    COLOURS.find((option) => option.id === data?.colour)?.value || '#171717';

  const hasCheckedForFileName = useRef(false);
  const { editActiveWidget } = useContext(WidgetContext);
  const { get } = useLiveDate();
  const [currentDate, setCurrentDate] = useState(new Date(get()));
  const [imageUrl, setImageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [skipInitialCall, setSkipInitialCall] = useState(true);
  const [imageEffect, setImageEffect] = useState(IMAGE_EFFECTS.STRETCH_TO_FIT);
  const time = {
    hour: currentDate.getHours(),
    minute: currentDate.getMinutes(),
    second: currentDate.getSeconds(),
  };

  useEffect(() => {
    if (data) {
      // Only on the first time the data has loaded from firestore, we will
      // check if there is a file name. We can't just check the imageFileName
      // on its own, as it will keep changing when images get uploaded.
      if (data.imageFileName && !hasCheckedForFileName.current) {
        setSkipInitialCall(false);
      }
      hasCheckedForFileName.current = true;
    }
  }, [data]);

  const { data: getCustomImageResponse } = useApi({
    requestFn: getCustomImage,
    args: [widgetId],
    skipInitialCall,
    onError: (error) => {
      Sentry.captureException(error);
    },
  });

  useEffect(() => {
    if (getCustomImageResponse) {
      setImageUrl(getCustomImageResponse);
    } else if (data?.backgroundURL) {
      setImageUrl(data?.backgroundURL);
    }
  }, [getCustomImageResponse, data?.backgroundURL]);

  useEffect(() => {
    if (data?.imageEffect) {
      setImageEffect(data?.imageEffect);
    }
  }, [data]);

  // update time every second
  useInterval(() => {
    if (onSelectedPage) {
      setCurrentDate(new Date(get()));
    }
  }, 1000);

  const standardHands = [
    {
      class: TIME_UNIT.H,
      rotationDegree: (time.hour * 3600 + time.minute * 60 + time.second) / 120,
    },
    {
      class: TIME_UNIT.M,
      rotationDegree: (time.minute * 60 + time.second) / 10,
    },
    {
      class: TIME_UNIT.S,
      rotationDegree: time.second * 6,
    },
  ];

  // hands
  const renderHands = (hands) =>
    hands.map((hand, i) => (
      <div
        key={i}
        className={c.containerBox}
        style={{
          transform: `rotate(${hand.rotationDegree}deg)`,
        }}
      >
        <div className={c[hand.class]}>
          <div
            className={`${c.handShadow} ${
              hand.class === 'second' ? c.long : null
            }`}
            style={{
              transform: `rotate(-${hand.rotationDegree}deg) translate(7px,0) rotate(${hand.rotationDegree}deg)`,
            }}
          />
        </div>
      </div>
    ));

  // markers
  const renderMarkerPairs = (degrees) =>
    degrees.map((degree, i) => (
      <div
        key={i}
        className={c.markersPair}
        style={{
          transform: `rotate(${degree}deg)`,
        }}
      />
    ));

  return (
    <div
      className={c.clock}
      style={{
        '--clock-colour': clockColour,
      }}
    >
      {editActiveWidget?.widgetId === widgetId &&
      editActiveWidget?.action === 'customiseClock' ? (
        <CustomiseClock
          widgetId={widgetId}
          data={data}
          updateData={updateData}
          escapeFocusRef={escapeSettingActionsFocusRef}
          imageUrl={imageUrl}
          setImageUrl={setImageUrl}
          setImageEffect={setImageEffect}
          imageEffect={imageEffect}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          isLegacyUrl={Boolean(data?.backgroundURL)}
        />
      ) : null}

      <div className={c.sizeContainer}>
        <div className={c.frame}>
          <div className={c.wrapper}>
            <div
              className={c.face}
              style={
                imageUrl
                  ? {
                      backgroundImage: `url(${imageUrl})`,
                      backgroundSize: IMAGE_EFFECTS_BG_SIZE[imageEffect],
                      filter:
                        imageEffect === IMAGE_EFFECTS.BLUR
                          ? 'blur(6px)'
                          : 'none',
                    }
                  : {}
              }
            />
            <div className={c.circleBottom} />
            <div className={c.indicators}>
              {renderMarkerPairs([60, 120, 210, 330])}
              <span>12</span>
              <div className={c.mid}>
                <span>9</span>
                <span>3</span>
              </div>
              <span>6</span>
            </div>
            <div className={c.hands}>{renderHands(standardHands)}</div>
            <div className={c.circleTop} />
          </div>
        </div>
      </div>
    </div>
  );
};

const ClockModule = {
  component: Clock,
  name: 'Clock',
  icon: WidgetIcon.Clock,
  previewImage: preview,
  previewBackgroundColor: '#E8EEF4',
  description: 'Decorate your Home with a customisable clock.',
  defaultSize: 'S',
  openEditMenuWhenAdded: true,
  additionalOptions: [
    {
      key: 'backgroundURL',
      editType: null,
      default: null,
    },
  ],
  additionalActions: [
    {
      action: 'customiseClock',
      icon: <Icon.Pencil size={20} />,
      text: 'Customise',
      haspopup: 'dialog',
    },
  ],
};

export default ClockModule;
